import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { connect } from 'react-redux';

import Breakpoints from 'lib/Breakpoints';
import { ACCESS_ENABLER_OBJ } from 'lib/tve';

import {
  hideProviderIFrameAction,
  hideProviderSelectAction,
  loadProvidersFeatured as loadFeaturedAction,
  loadProvidersOptions as loadOptionsAction,
} from 'redux/modules/tve';

import Modal from 'components/Modal';
import SignInIFrame from 'components/TVE/SignInIFrame';

import BackButton from './BackButton';
import FeaturedGrid from './FeaturedGrid';
import Loader from './Loader';
import Map from './Map';
import Options from './Options';
import Results from './Results';
import Search from './Search';

import { block } from './helper';
import './styles.themed.scss';

/**
 * Maps the Redux store state to component props.
 *
 * @param {object} state - The Redux store state.
 * @param {object} state.tve - The TVE module state slice.
 * @returns {object} Props to inject into the ProviderSelect component.
 */
const mapStateToProps = ({ tve }) => ({
  vertical: 'msnbc',
  featured: tve.featured,
  options: tve.options,
  providerIFrameVisible: tve.providerIFrameVisible,
});


/**
 * Maps Redux dispatch actions to component props.
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @returns {object} Dispatchable actions for the ProviderSelect component.
 */
const mapActionsToProps = (dispatch) => ({
  /**
   * Hides the provider selection modal and iframe.
   * @returns {void}
   */
  hideProviderSelect: () => {
    dispatch(hideProviderSelectAction());
    dispatch(hideProviderIFrameAction());
  },
  /**
   * Hides the provider sign-in iframe.
   * @returns {void}
   */
  hideProviderIFrame: () => dispatch(hideProviderIFrameAction()),
  /**
   * Loads the featured TV providers for a given vertical.
   * @param {string} vertical - The content vertical.
   * @returns {void}
   */
  loadFeatured: (vertical) => dispatch(loadFeaturedAction(vertical)),
  /**
   * Loads the list of all available provider options for a given vertical.
   * @param {string} vertical - The content vertical.
   * @returns {void}
   */
  loadOptions: (vertical) => dispatch(loadOptionsAction(vertical)),
});

/**
 * ProviderSelect component for TVE authentication flow.
 *
 * @param {object} props - Component props.
 * @param {Array} props.featured - Array of featured TV providers.
 * @param {Array} props.options - Array of all provider options.
 * @param {Function} props.hideProviderIFrame - Function to hide provider iframe.
 * @param {Function} props.hideProviderSelect - Function to hide provider select modal.
 * @param {Function} props.loadOptions - Function to load provider options.
 * @param {Function} props.loadFeatured - Function to load featured providers.
 * @param {object} props.iframeRef - Ref for the iframe component.
 * @param {boolean} props.providerIFrameVisible - Whether the provider iframe is visible.
 * @param {string} [props.redirectUrl] - Optional redirect URL for authentication.
 * @param {string} props.vertical - Content vertical.
 * @returns {JSX.Element} Provider selection modal.
 */
const ProviderSelect = ({
  featured = [],
  options = [],
  hideProviderIFrame = () => {},
  hideProviderSelect = () => {},
  loadOptions,
  loadFeatured,
  iframeRef,
  providerIFrameVisible,
  redirectUrl = null,
  vertical,
}) => {
  const [searchResults, setSearchResults] = useState([]);
  const [showFeatured, setShowFeatured] = useState(true);
  const [showFullList, setShowFullList] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [fullListType, setFullListType] = useState(null);

  const inputRef = useRef(null);
  const searchTO = useRef(null);

  const getFullListType = useCallback(() => {
    if (Breakpoints.isSorM()) {
      return 'select';
    }
    return 'list';
  }, []);

  const selectProvider = useCallback(
    (id) => {
      const AE = window[ACCESS_ENABLER_OBJ];
      AE.setSelectedProvider(null);
      AE.getAuthentication(redirectUrl);
      AE.setSelectedProvider(id);
    },
    [redirectUrl],
  );

  const onBreakpointChange = useCallback(() => {
    setFullListType(getFullListType());
  }, [getFullListType]);

  useEffect(() => {
    if (!featured.length) {
      loadFeatured(vertical);
    }
    if (!options.length) {
      loadOptions(vertical);
    }

    Breakpoints.getMediumMQL().addListener(onBreakpointChange);
    Breakpoints.getLargeMQL().addListener(onBreakpointChange);

    return () => {
      Breakpoints.getMediumMQL().removeListener(onBreakpointChange);
      Breakpoints.getLargeMQL().removeListener(onBreakpointChange);
    };
  }, []);

  useEffect(() => {
    if (providerIFrameVisible) {
      setShowLoader(false);
    }
  }, [providerIFrameVisible]);

  const handleGoBack = useCallback(
    (e) => {
      e.preventDefault();
      hideProviderIFrame();
      setSearchResults([]);
      setShowFeatured(true);
      setShowFullList(false);
      setShowLoader(false);
    },
    [hideProviderIFrame],
  );

  const handleShowFullList = useCallback(
    (e) => {
      e.preventDefault();
      setShowFeatured(false);
      setShowFullList(true);
      setShowLoader(false);
      setSearchResults(options);
    },
    [options],
  );

  const handleSelectProvider = useCallback(
    (e) => {
      e.preventDefault();
      const { value } = e.target;
      setFullListType(getFullListType());
      setSearchResults([]);
      setShowFeatured(false);
      setShowFullList(false);
      setShowLoader(true);
      selectProvider(value);
    },
    [getFullListType, selectProvider],
  );

  const handleSearch = useCallback(
    (e, filter = false) => {
      e.preventDefault();

      if (searchTO.current) {
        clearTimeout(searchTO.current);
      }

      searchTO.current = setTimeout(() => {
        const lower = inputRef.current?.value?.trim().toLowerCase() || '';
        let results = filter ? options : [];
        if (lower.length) {
          results = options.filter((opt) => opt.displayName.toLowerCase().includes(lower));
        }
        setSearchResults(results);
      }, 250);
    },
    [options],
  );

  const handleSearchClear = useCallback(
    (e) => {
      e.preventDefault();
      setSearchResults(showFullList ? options : []);
      if (inputRef.current) inputRef.current.value = '';
    },
    [showFullList, options],
  );

  const renderOverlay = useCallback(() => {
    const fullListTypeValue = fullListType || getFullListType();

    return (
      <div className={block}>
        <button
          type="button"
          onClick={hideProviderSelect}
          className={`${block}__button ${block}__button__close`}
        >
          <span className="icon icon-close" />
        </button>
        <h1 className={`${block}__heading-h1`}>Sign In</h1>
        <p className={`${block}__description`}>
          Sign in with the same username and password you use to access your TV provider account.
        </p>
        <Map step2={showLoader || providerIFrameVisible} />

        {showFeatured && (
          <>
            <FeaturedGrid featured={featured} onClick={handleSelectProvider} />
            <p className={`${block}__search-text`}>
              <strong>Don’t see your provider?</strong>
              {' '}
              Search Below, or select from the
              {' '}
              <button
                type="button"
                onClick={handleShowFullList}
                className={`${block}__button ${block}__button__full-list`}
              >
                full list
              </button>
            </p>
            <Search
              inputRef={inputRef}
              onClickClear={handleSearchClear}
              onChange={handleSearch}
            />
            <Results onClick={selectProvider} results={searchResults} />
          </>
        )}

        {showFullList && (
          <>
            <BackButton
              text="Full List of TV Providers Below"
              onClick={handleGoBack}
            />
            {fullListTypeValue === 'select' && (
              <Options options={options} onChange={handleSelectProvider} />
            )}
            {fullListTypeValue === 'list' && (
              <>
                <Search
                  filter
                  inputRef={inputRef}
                  onClickClear={handleSearchClear}
                  onChange={handleSearch}
                  results={searchResults}
                />
                <Results fill onClick={selectProvider} results={searchResults} />
              </>
            )}
          </>
        )}

        {providerIFrameVisible && (
          <>
            <BackButton text="Verify Account" onClick={handleGoBack} />
            <SignInIFrame ref={iframeRef} />
          </>
        )}

        {showLoader && <Loader />}
      </div>
    );
  }, [
    fullListType,
    getFullListType,
    hideProviderSelect,
    showLoader,
    providerIFrameVisible,
    showFeatured,
    showFullList,
    featured,
    options,
    handleSelectProvider,
    handleShowFullList,
    handleGoBack,
    handleSearch,
    handleSearchClear,
    selectProvider,
    searchResults,
  ]);

  return (
    <Modal preventBackgroundScroll>
      {renderOverlay()}
    </Modal>
  );
};

export default connect(mapStateToProps, mapActionsToProps)(ProviderSelect);

